import React, { FC, useEffect, useRef } from 'react';
import { Box, Typography, TextField, FormControl, Select, InputLabel, MenuItem, Button } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';

import { palette } from '../../theme';
import COETrainingDurationInput from '../coe-training-duration-input/coe-training-duration-input.component';
import COEActionBarComponent from '../coe-action-bar/coe-action-bar.component';
import {
  getSportMappings,
  getSportsOptionsFromMapping,
  getSportsTypeOptionsFromMapping,
} from '../../helpers/app-context.helpers';
import { getMeasurementUnit } from '../../helpers/general.helpers';

const EQUIPMENT_PERFORMANCE_LABELS: Record<number, string> = {
  1: 'Bad',
  2: 'Okay',
  3: 'Good',
  4: 'Great',
  5: 'Excellent',
};

function getPerformanceLabel(value: number): string {
  return EQUIPMENT_PERFORMANCE_LABELS[value] ?? String(value);
}

const PERFORMANCE_OPTIONS = [1, 2, 3, 4, 5].map((value) => ({
  value,
  label: getPerformanceLabel(value),
}));

export type AddEntryFormFields =
  | 'race_name'
  | 'race_date'
  | 'race_location'
  | 'race_distance'
  | 'race_duration'
  | 'race_sport'
  | 'race_sport_type'
  | 'race_type'
  | 'result'
  | 'equipment_performance'
  | 'race_note';

export interface AddEntryForm {
  race_name: string;
  race_date: string;
  race_location: string;
  race_distance: string;
  race_duration: string;
  race_sport: string;
  race_sport_type: string;
  race_type: string;
  result: string;
  equipment_performance: number | '';
  race_note: string;
}

interface AddRaceLogEntryFormProps {
  form: AddEntryForm;
  onFormChange: (field: AddEntryFormFields, value: string | number) => void;
  onAddEntry?: () => void;
  isSubmitting?: boolean;
}

const AddRaceLogEntryForm: FC<AddRaceLogEntryFormProps> = ({
  form,
  onFormChange,
  onAddEntry,
  isSubmitting = false,
}) => {
  const sportsMappings = getSportMappings();
  const hasInitializedDateRef = useRef(false);
  const dateToTimeString = (isoString: string): string => {
    if (!isoString) return '';
    const d = dayjs(isoString);
    if (!d.isValid()) return '';
    return d.format('HH:mm');
  };

  const combineDateAndTime = (date: Date | null, time: string, fallbackIso?: string): string => {
    const base = fallbackIso && dayjs(fallbackIso).isValid() ? dayjs(fallbackIso) : dayjs();

    if (!date && !time) {
      return fallbackIso ?? '';
    }

    let result = base;

    if (date) {
      result = dayjs(date).hour(base.hour()).minute(base.minute()).second(0).millisecond(0);
    }

    if (time) {
      const [hoursStr, minutesStr] = time.split(':');
      const hours = Number(hoursStr);
      const minutes = Number(minutesStr);
      if (!Number.isNaN(hours) && !Number.isNaN(minutes)) {
        result = result.hour(hours).minute(minutes).second(0).millisecond(0);
      }
    }

    return result.toISOString();
  };

  useEffect(() => {
    if (!hasInitializedDateRef.current && !form.race_date) {
      const nowIso = dayjs().toISOString();
      onFormChange('race_date', nowIso);
      hasInitializedDateRef.current = true;
    }
  }, [form.race_date]);

  return (
    <Box
      sx={{
        marginBottom: 2,
        padding: 1,
        backgroundColor: palette.background.subtle,
        borderRadius: 1,
        border: `1px solid ${palette.grey[200]}`,
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gap: 2,
        alignItems: 'flex-start',
      }}
    >
      <TextField
        size="small"
        label="Event name"
        value={form.race_name}
        onChange={(e) => onFormChange('race_name', e.target.value)}
      />
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <TextField
          size="small"
          label="Time"
          type="time"
          value={dateToTimeString(form.race_date)}
          onChange={(e) => {
            const iso = combineDateAndTime(
              form.race_date ? dayjs(form.race_date).toDate() : null,
              e.target.value,
              form.race_date
            );
            onFormChange('race_date', iso);
          }}
          inputProps={{ step: 300 }}
        />
        <DatePicker
          label="Date"
          value={form.race_date ? dayjs(form.race_date).toDate() : null}
          onChange={(date: Date | null) => {
            const iso = combineDateAndTime(date, dateToTimeString(form.race_date), form.race_date);
            onFormChange('race_date', iso);
          }}
          renderInput={(params) => <TextField {...params} size="small" />}
        />
      </LocalizationProvider>
      <TextField
        size="small"
        label="Location"
        value={form.race_location}
        onChange={(e) => onFormChange('race_location', e.target.value)}
      />
      <TextField
        size="small"
        label="Race type"
        value={form.race_type}
        onChange={(e) => onFormChange('race_type', e.target.value)}
      />
      <TextField
        size="small"
        label="Result (placing)"
        type="number"
        value={form.result}
        onChange={(e) => onFormChange('result', e.target.value)}
      />
      <Box sx={{ gridColumn: '1 / -1', mt: 1 }}>
        <Typography variant="caption" sx={{ color: palette.text.secondary }}>
          Sport
        </Typography>
        <COEActionBarComponent
          items={getSportsOptionsFromMapping(sportsMappings)}
          selectedItem={form.race_sport}
          setSelectedItem={(item: any) => onFormChange('race_sport', item)}
          padding="3px 10px"
          fontSize={14}
          marginTop={0}
        />
      </Box>
      <Box sx={{ gridColumn: '1 / -1', mt: 1 }}>
        <Typography variant="caption" sx={{ color: palette.text.secondary }}>
          Sport type
        </Typography>
        <COEActionBarComponent
          items={getSportsTypeOptionsFromMapping(sportsMappings, form.race_sport)}
          selectedItem={form.race_sport_type}
          setSelectedItem={(item: any) => onFormChange('race_sport_type', item)}
          padding="3px 10px"
          fontSize={14}
          marginTop={0}
        />
      </Box>
      <COETrainingDurationInput
        value={Number(form.race_duration || 0)}
        onChange={(seconds: number) => onFormChange('race_duration', String(seconds))}
      />
      <TextField
        size="small"
        label="Distance"
        type="number"
        value={form.race_distance}
        onChange={(e) => onFormChange('race_distance', e.target.value)}
        InputProps={{
          endAdornment: (
            <Typography variant="caption" sx={{ color: palette.text.secondary }}>
              {getMeasurementUnit()}
            </Typography>
          ),
        }}
      />
      <FormControl size="small" variant="outlined" sx={{ minWidth: 160 }}>
        <InputLabel id="create-equipment-performance-label">Ski Performance</InputLabel>
        <Select
          labelId="create-equipment-performance-label"
          label="Ski Performance"
          value={form.equipment_performance === '' ? '' : form.equipment_performance}
          onChange={(e) => onFormChange('equipment_performance', e.target.value === '' ? '' : Number(e.target.value))}
        >
          <MenuItem value="">
            <em>Select</em>
          </MenuItem>
          {PERFORMANCE_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Box sx={{ gridColumn: '1 / -1' }}>
        <TextField
          size="small"
          label="Notes"
          multiline
          minRows={3}
          value={form.race_note}
          onChange={(e) => onFormChange('race_note', e.target.value)}
          fullWidth
        />
      </Box>
      <Box sx={{ gridColumn: '1 / -1', mt: 1 }}>
        <Button variant="contained" type="button" onClick={() => onAddEntry?.()} disabled={isSubmitting}>
          Create entry
        </Button>
      </Box>
    </Box>
  );
};

export default AddRaceLogEntryForm;
